import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMG } from "@/data/site";
import { useLang } from "@/context/LanguageContext";

const TriangleHero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });

  const scale = useTransform(scrollYProgress, [0, 1], [1, 16]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.35, 1]);
  const introOpacity = useTransform(scrollYProgress, [0, 0.22], [1, 0]);
  const finalOpacity = useTransform(scrollYProgress, [0.62, 0.95], [0, 1]);
  const finalY = useTransform(scrollYProgress, [0.62, 0.95], [40, 0]);
  const hintOpacity = useTransform(scrollYProgress, [0, 0.08], [1, 0]);

  return (
    <section ref={ref} className="relative h-[260vh]" data-testid="triangle-hero">
      <div className="sticky top-0 h-screen overflow-hidden bg-ink flex items-center justify-center">
        {/* giant outlined wordmark behind */}
        <motion.div style={{ opacity: introOpacity }} className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-stroke font-display font-black uppercase leading-none" style={{ fontSize: "23vw" }}>
            grabarte
          </span>
        </motion.div>

        {/* triangle mask revealing cinematic image */}
        <motion.div
          style={{ scale }}
          className="relative clip-triangle overflow-hidden"
          data-testid="hero-triangle"
        >
          <div className="w-[42vh] h-[42vh]">
            <motion.img
              style={{ scale: imgScale }}
              src={IMG.audiovisualHero}
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-ink/25" />
          </div>
        </motion.div>

        {/* intro text over triangle */}
        <motion.div style={{ opacity: introOpacity }} className="absolute z-10 text-center px-6 pointer-events-none">
          <p className="font-display text-xs md:text-sm font-bold uppercase tracking-[0.35em] text-brand-gold mb-5">
            {t({ es: "Madrid · desde 2010", en: "Madrid · since 2010" })}
          </p>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-black uppercase text-white leading-[0.95] tracking-tight">
            {t({ es: "Marketing", en: "Marketing" })}
            <span className="text-brand-gold"> + </span>
            {t({ es: "Cine", en: "Cinema" })}
          </h1>
        </motion.div>

        {/* final reveal content when image fills the screen */}
        <motion.div
          style={{ opacity: finalOpacity, y: finalY }}
          className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center px-6"
        >
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(23,23,38,0.85), rgba(23,23,38,0.15))" }} />
          <div className="relative">
            <p className="font-display text-xs md:text-sm font-bold uppercase tracking-[0.35em] text-brand-gold mb-6">
              {t({ es: "Estudio de desarrollo", en: "Development studio" })}
            </p>
            <h2 className="font-display text-3xl md:text-6xl lg:text-7xl font-black uppercase text-white leading-[0.95] max-w-5xl text-balance">
              {t({ es: "Creamos historias que merecen ser contadas", en: "We create stories worth telling" })}
            </h2>
          </div>
        </motion.div>

        {/* scroll hint */}
        <motion.div style={{ opacity: hintOpacity }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-2">
          <span className="font-display text-[0.65rem] font-bold uppercase tracking-[0.3em] text-white/50">Scroll</span>
          <span className="w-px h-10 bg-gradient-to-b from-white/50 to-transparent" />
        </motion.div>
      </div>
    </section>
  );
};

export default TriangleHero;
